// Project chat — single #general channel for v1. Plain-text bodies with
// auto-linkified URLs; image-only attachments via drag-drop, paste, or
// the paperclip picker. Realtime via socket.io message:event broadcasts.
"use strict";

(function () {
  function init() {
    var root = document.querySelector('.chat[data-channel-id]');
    if (!root) return;

    var orgSlug   = root.getAttribute('data-org-slug');
    var projectId = root.getAttribute('data-project-id');
    var channelId = root.getAttribute('data-channel-id');
    var csrf      = root.getAttribute('data-csrf');
    var currentUserId = parseInt(root.getAttribute('data-current-user-id'), 10) || 0;

    var stream     = document.getElementById('chatStream');
    var composer   = document.getElementById('chatComposer');
    var textarea   = document.getElementById('chatTextarea');
    var fileInput  = document.getElementById('chatFiles');
    var tray       = document.getElementById('chatAttachmentsTray');
    var sendBtn    = composer.querySelector('.chat__send');
    var mentionList = document.getElementById('mentionAutocomplete');

    // Roster of mentionable users + agents, embedded server-side in the
    // .chat data-roster attribute. Format: [{ kind:'user'|'agent', id, label, role? }]
    var roster = [];
    try { roster = JSON.parse(root.getAttribute('data-roster') || '[]'); }
    catch (_) { roster = []; }

    // Deep-link prefill (e.g. from the Docs empty-state CTA). Populates
    // the composer with a starter body; nothing is auto-submitted.
    var prefill = root.getAttribute('data-prefill') || '';
    if (prefill && textarea && !textarea.value) {
      textarea.value = prefill;
      // Cursor at end so the user can append immediately.
      try { textarea.setSelectionRange(prefill.length, prefill.length); }
      catch (_) {}
      try { textarea.focus(); } catch (_) {}
    }

    // Project Home (UX revamp) renders entries on the server, cards included;
    // there this file only composes. project-home.js owns appends.
    var homeMode = root.getAttribute('data-thread-mode') === 'home';

    var pending = [];   // [{file, previewUrl}]
    var seenIds = {};   // dedupe across echo + manual fetch

    // ── Mobile sidebar drawer ────────────────────────────────────────────
    // At narrow widths the .chat-sidebar is positioned off-canvas. The
    // Channels toggle button + scrim toggle a class on .chat-layout that
    // slides the sidebar in. CSS does the visual work; this is just the
    // state machine.
    var chatLayout    = document.getElementById('chatLayout');
    var sidebarEl     = document.getElementById('chatSidebar');
    var sidebarScrim  = document.getElementById('chatSidebarScrim');
    var channelsToggle = document.getElementById('chatChannelsToggle');
    function openSidebarDrawer() {
      if (!chatLayout) return;
      chatLayout.classList.add('chat-layout--sidebar-open');
      if (channelsToggle) channelsToggle.setAttribute('aria-expanded', 'true');
    }
    function closeSidebarDrawer() {
      if (!chatLayout) return;
      chatLayout.classList.remove('chat-layout--sidebar-open');
      if (channelsToggle) channelsToggle.setAttribute('aria-expanded', 'false');
    }
    if (channelsToggle) {
      channelsToggle.addEventListener('click', function () {
        if (chatLayout && chatLayout.classList.contains('chat-layout--sidebar-open')) {
          closeSidebarDrawer();
        } else {
          openSidebarDrawer();
        }
      });
    }
    if (sidebarScrim) sidebarScrim.addEventListener('click', closeSidebarDrawer);
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && chatLayout && chatLayout.classList.contains('chat-layout--sidebar-open')) {
        closeSidebarDrawer();
      }
    });
    // Sidebar link click implicitly closes via page navigation, but if
    // any future link is in-page (e.g. JS-driven channel switch), we
    // close the drawer here too.
    if (sidebarEl) {
      sidebarEl.addEventListener('click', function (e) {
        var link = e.target && e.target.closest ? e.target.closest('a') : null;
        if (link) closeSidebarDrawer();
      });
    }

    // ── URL auto-linkification ───────────────────────────────────────────
    var URL_RE = /\b((?:https?:\/\/|www\.)[^\s<>"]+[^\s<>".,!?;:)\]])/gi;
    function escapeHtml(s) {
      return s.replace(/[&<>"']/g, function (c) {
        return ({ '&':'&amp;', '<':'&lt;', '>':'&gt;', '"':'&quot;', "'":'&#39;' })[c];
      });
    }
    function linkifyEscaped(s) {
      return escapeHtml(s).replace(URL_RE, function (url) {
        var href = url.indexOf('http') === 0 ? url : 'http://' + url;
        return '<a href="' + href + '" target="_blank" rel="noopener noreferrer">' + url + '</a>';
      });
    }

    // ── Message render ───────────────────────────────────────────────────
    function fmtTime(iso) {
      var d = new Date(iso);
      if (isNaN(d.getTime())) return '';
      var now = new Date();
      var sameDay = d.toDateString() === now.toDateString();
      if (sameDay) {
        return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
      }
      return d.toLocaleDateString([], { month: 'short', day: 'numeric' }) +
             ' ' + d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    }

    function attachmentUrl(attId) {
      return '/org/' + orgSlug + '/projects/' + projectId + '/attachments/' + attId;
    }

    function renderMessage(m) {
      var li = document.createElement('li');
      li.className = 'chat__msg';
      // System messages (task → Ready, needs-input notifications, etc.)
      // render distinctly: no avatar, muted italic body, no delete
      // affordance. Author block is just a small "System" label.
      var isSystem = m.kind === 'system';
      if (isSystem) li.classList.add('chat__msg--system');
      if (m.author_agent_id) li.classList.add('chat__msg--agent');
      li.setAttribute('data-message-id', m.id);

      var head = document.createElement('div');
      head.className = 'chat__msg-head';
      var author = document.createElement('span');
      author.className = 'chat__msg-author';
      if (isSystem) {
        author.innerHTML = '<span class="chat__msg-system-tag">System</span>';
      } else if (m.author_agent_id) {
        author.innerHTML = '<span class="chat__msg-agent-tag">Agent</span> ' + escapeHtml(m.author_agent_label || '(agent)');
      } else {
        author.textContent = m.author_user_label || '(unknown)';
      }
      var time = document.createElement('span');
      time.className = 'chat__msg-time';
      time.textContent = fmtTime(m.created_on);
      head.appendChild(author);
      head.appendChild(time);

      // Author can delete own message — not applicable to system messages.
      if (!isSystem && currentUserId && m.author_user_id === currentUserId) {
        var del = document.createElement('button');
        del.type = 'button';
        del.className = 'chat__msg-delete';
        del.title = 'Delete message';
        del.textContent = '×';
        del.addEventListener('click', function () {
          if (!confirm('Delete this message?')) return;
          deleteMessage(m.id);
        });
        head.appendChild(del);
      }
      li.appendChild(head);

      if (m.body) {
        var body = document.createElement('div');
        body.className = 'chat__msg-body';
        body.innerHTML = linkifyEscaped(m.body);
        li.appendChild(body);
      }

      if (m.attachments && m.attachments.length) {
        var grid = document.createElement('div');
        grid.className = 'chat__msg-attachments';
        m.attachments.forEach(function (a) {
          var aEl = document.createElement('a');
          aEl.href = attachmentUrl(a.id);
          aEl.target = '_blank';
          aEl.rel = 'noopener';
          aEl.className = 'chat__msg-attachment';
          var img = document.createElement('img');
          img.src = attachmentUrl(a.id);
          img.alt = a.filename || 'image';
          img.loading = 'lazy';
          aEl.appendChild(img);
          grid.appendChild(aEl);
        });
        li.appendChild(grid);
      }
      return li;
    }

    function appendMessage(m, opts) {
      if (seenIds[m.id]) return;
      seenIds[m.id] = true;
      var li = renderMessage(m);
      // Strip the "Loading…" placeholder if present
      var loader = stream.querySelector('.chat__loading');
      if (loader) loader.parentNode.removeChild(loader);
      var atBottom = isStreamAtBottom();
      stream.appendChild(li);
      if (opts && opts.flash) li.classList.add('chat__msg--flash');
      if (atBottom || (opts && opts.scroll)) scrollToBottom();
    }

    function isStreamAtBottom() {
      // Treat "within 80px of bottom" as at-bottom
      return (stream.scrollHeight - stream.scrollTop - stream.clientHeight) < 80;
    }

    function scrollToBottom() {
      stream.scrollTop = stream.scrollHeight;
    }

    function tombstoneMessage(messageId) {
      var li = stream.querySelector('.chat__msg[data-message-id="' + messageId + '"]');
      if (!li) return;
      li.classList.add('chat__msg--deleted');
      var body = li.querySelector('.chat__msg-body');
      if (body) body.innerHTML = '<em class="muted">deleted</em>';
      var grid = li.querySelector('.chat__msg-attachments');
      if (grid) grid.parentNode.removeChild(grid);
      var del = li.querySelector('.chat__msg-delete');
      if (del) del.parentNode.removeChild(del);
    }

    // ── Initial fetch ────────────────────────────────────────────────────
    function loadInitial() {
      // In Workspace mode the server has already rendered the thread, and that
      // render contains ACTIVITY entries this message-only renderer knows
      // nothing about. Fetching here would clear the stream and silently drop
      // them. workspace.js owns history and paging on that surface; this file
      // keeps the composer, mentions, attachments and live message appends.
      if (root.getAttribute('data-thread-mode') === 'workspace' || homeMode) return;

      var url = '/org/' + orgSlug + '/projects/' + projectId
              + '/channels/' + channelId + '/messages.json?limit=100';
      fetch(url, {
        method: 'GET',
        credentials: 'same-origin',
        headers: { 'Accept': 'application/json' }
      }).then(function (r) {
        if (!r.ok) throw new Error('messages load failed: ' + r.status);
        return r.json();
      }).then(function (data) {
        stream.innerHTML = '';
        var msgs = (data.messages || []).slice().reverse();   // server returns newest-first
        if (msgs.length === 0) {
          var empty = document.createElement('li');
          empty.className = 'chat__loading';
          empty.textContent = 'No messages yet — say hi.';
          stream.appendChild(empty);
        } else {
          msgs.forEach(function (m) { appendMessage(m); });
          scrollToBottom();
        }
      }).catch(function (err) {
        stream.innerHTML = '';
        var li = document.createElement('li');
        li.className = 'chat__loading muted';
        li.textContent = 'Could not load messages: ' + err.message;
        stream.appendChild(li);
      });
    }

    // ── Compose / send ───────────────────────────────────────────────────
    function renderTray() {
      tray.innerHTML = '';
      if (pending.length === 0) { tray.hidden = true; return; }
      tray.hidden = false;
      pending.forEach(function (p, i) {
        var item = document.createElement('div');
        item.className = 'chat__tray-item';
        var img = document.createElement('img');
        img.src = p.previewUrl;
        item.appendChild(img);
        var rm = document.createElement('button');
        rm.type = 'button';
        rm.className = 'chat__tray-remove';
        rm.textContent = '×';
        rm.title = 'Remove';
        rm.addEventListener('click', function () {
          URL.revokeObjectURL(p.previewUrl);
          pending.splice(i, 1);
          renderTray();
        });
        item.appendChild(rm);
        tray.appendChild(item);
      });
    }

    function addFiles(fileList) {
      var imgTypes = /^image\/(png|jpeg|gif|webp)$/;
      Array.prototype.forEach.call(fileList, function (f) {
        if (pending.length >= 4) return;
        if (!imgTypes.test(f.type)) return;            // image-only client guard
        if (f.size > 5 * 1024 * 1024) {
          alert(f.name + ' is over 5MB.');
          return;
        }
        pending.push({ file: f, previewUrl: URL.createObjectURL(f) });
      });
      renderTray();
    }

    fileInput.addEventListener('change', function () {
      addFiles(fileInput.files);
      fileInput.value = '';
    });

    // Drag-drop into the composer area
    ['dragenter', 'dragover'].forEach(function (evt) {
      composer.addEventListener(evt, function (e) {
        if (!e.dataTransfer || !e.dataTransfer.types || e.dataTransfer.types.indexOf('Files') < 0) return;
        e.preventDefault(); e.stopPropagation();
        composer.classList.add('chat__composer--dragover');
      });
    });
    ['dragleave', 'drop'].forEach(function (evt) {
      composer.addEventListener(evt, function (e) {
        composer.classList.remove('chat__composer--dragover');
        if (evt === 'drop') {
          e.preventDefault(); e.stopPropagation();
          if (e.dataTransfer && e.dataTransfer.files) addFiles(e.dataTransfer.files);
        }
      });
    });

    // Paste-from-clipboard image handling
    textarea.addEventListener('paste', function (e) {
      if (!e.clipboardData || !e.clipboardData.items) return;
      var imgs = [];
      Array.prototype.forEach.call(e.clipboardData.items, function (it) {
        if (it.kind === 'file') {
          var f = it.getAsFile();
          if (f) imgs.push(f);
        }
      });
      if (imgs.length) {
        e.preventDefault();
        addFiles(imgs);
      }
    });

    // ── @-mention autocomplete ──────────────────────────────────────────
    // When the user types "@", show a filtered dropdown of mentionable
    // entities (roster). Arrow keys navigate; Enter or Tab inserts;
    // Escape closes. The textarea is the source of truth for what gets
    // sent — the dropdown just helps the user spell the name correctly.
    var mention = { active: false, anchor: -1, query: '', items: [], hi: 0 };

    function closeMention() {
      mention.active = false;
      mention.items = [];
      mention.hi = 0;
      if (mentionList) {
        mentionList.hidden = true;
        mentionList.innerHTML = '';
      }
    }

    function refreshMentionUI() {
      if (!mentionList || !mention.active) return;
      mentionList.innerHTML = '';
      if (mention.items.length === 0) {
        mentionList.hidden = true;
        return;
      }
      mention.items.forEach(function (it, idx) {
        var li = document.createElement('li');
        li.className = 'mention-autocomplete__item';
        if (idx === mention.hi) li.classList.add('mention-autocomplete__item--active');
        li.setAttribute('role', 'option');
        li.setAttribute('data-idx', String(idx));
        var label = document.createElement('span');
        label.className = 'mention-autocomplete__label';
        label.textContent = '@' + it.label;
        li.appendChild(label);
        if (it.kind === 'agent') {
          var tag = document.createElement('span');
          tag.className = 'mention-autocomplete__tag';
          tag.textContent = it.role === 'developer'
            ? 'Developer'
            : (it.role === 'devops' ? 'Devops' : 'Analyst');
          li.appendChild(tag);
        }
        li.addEventListener('mousedown', function (e) {
          // mousedown not click — click would fire after textarea blur
          e.preventDefault();
          insertMention(idx);
        });
        mentionList.appendChild(li);
      });
      mentionList.hidden = false;
    }

    // Detect whether the cursor is currently inside an @-mention token
    // and update the dropdown accordingly. The anchor is the position of
    // the @-sign in the textarea value; the query is everything from
    // anchor+1 up to the cursor.
    function recalcMention() {
      var pos = textarea.selectionStart;
      var val = textarea.value;
      // Walk backwards from cursor to find the most recent "@" that's
      // either at start-of-string or preceded by whitespace.
      var i = pos - 1;
      var found = -1;
      while (i >= 0) {
        var ch = val.charAt(i);
        if (ch === '@') {
          var prev = i > 0 ? val.charAt(i - 1) : '';
          if (i === 0 || /\s/.test(prev)) {
            found = i;
          }
          break;
        }
        // Token chars: letters, digits, dot, dash, underscore. Anything
        // else (space, punctuation) ends the search.
        if (!/[A-Za-z0-9._-]/.test(ch)) break;
        i--;
      }
      if (found < 0) { closeMention(); return; }
      var query = val.slice(found + 1, pos);
      mention.active = true;
      mention.anchor = found;
      mention.query = query;
      // Filter roster: case-insensitive prefix or substring match.
      var q = query.toLowerCase();
      mention.items = roster.filter(function (r) {
        if (!q) return true;
        return r.label && r.label.toLowerCase().indexOf(q) !== -1;
      }).slice(0, 8); // cap to 8 visible
      if (mention.hi >= mention.items.length) mention.hi = 0;
      refreshMentionUI();
    }

    function insertMention(idx) {
      if (!mention.active) return;
      var it = mention.items[idx];
      if (!it) return;
      var pos = textarea.selectionStart;
      var val = textarea.value;
      // Replace from anchor through current cursor with "@<label> ".
      var before = val.slice(0, mention.anchor);
      var after  = val.slice(pos);
      var insert = '@' + it.label + ' ';
      textarea.value = before + insert + after;
      var newPos = before.length + insert.length;
      textarea.setSelectionRange(newPos, newPos);
      closeMention();
      textarea.focus();
    }

    textarea.addEventListener('input', recalcMention);
    textarea.addEventListener('click', recalcMention);
    textarea.addEventListener('keyup', function (e) {
      // Navigation keys handled in keydown; only refresh on character keys.
      if (['ArrowUp', 'ArrowDown', 'Enter', 'Tab', 'Escape'].indexOf(e.key) >= 0) return;
      recalcMention();
    });
    textarea.addEventListener('blur', function () {
      // Delay so a mousedown on a dropdown item can fire first.
      setTimeout(closeMention, 150);
    });

    // Enter sends; Shift+Enter adds a new line. Mention dropdown
    // intercepts: ArrowUp/Down navigate, Enter/Tab insert, Escape closes.
    textarea.addEventListener('keydown', function (e) {
      if (mention.active && mention.items.length > 0) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          mention.hi = (mention.hi + 1) % mention.items.length;
          refreshMentionUI();
          return;
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          mention.hi = (mention.hi - 1 + mention.items.length) % mention.items.length;
          refreshMentionUI();
          return;
        }
        if (e.key === 'Enter' || e.key === 'Tab') {
          e.preventDefault();
          insertMention(mention.hi);
          return;
        }
        if (e.key === 'Escape') {
          e.preventDefault();
          closeMention();
          return;
        }
      }
      // Skip while an IME is composing (Enter confirms the composition).
      if (e.key === 'Enter' && !e.shiftKey && !e.isComposing && e.keyCode !== 229) {
        e.preventDefault();
        if (textarea.value.trim()) composer.requestSubmit();
      }
    });

    composer.addEventListener('submit', function (e) {
      e.preventDefault();
      sendMessage();
    });

    // Reply to an agent's message (agent activation: a reply wakes the
    // analyst). The next message carries reply_to; the bar can cancel it.
    var replyTo = null;
    var replyBar = document.createElement('div');
    replyBar.className = 'chat__replying';
    replyBar.hidden = true;
    replyBar.innerHTML = '<span class="chat__replying-text"></span><button type="button" class="chat__replying-x" aria-label="Cancel reply">\u00d7</button>';
    composer.insertBefore(replyBar, composer.firstChild);
    function setReply(id, name) {
      replyTo = id;
      replyBar.hidden = !id;
      replyBar.querySelector('.chat__replying-text').textContent = id ? 'Replying to ' + (name || 'the message') : '';
    }
    replyBar.querySelector('.chat__replying-x').addEventListener('click', function () { setReply(null); textarea.focus(); });
    document.addEventListener('click', function (e) {
      var btn = e.target.closest && e.target.closest('.te__reply');
      if (!btn) return;
      setReply(btn.getAttribute('data-reply-to'), btn.getAttribute('data-reply-name'));
      textarea.focus();
    });

    function sendMessage() {
      var body = (textarea.value || '').trim();
      if (!body && pending.length === 0) return;
      if (sendBtn.disabled) return;
      sendBtn.disabled = true;

      var fd = new FormData();
      fd.append('_csrf', csrf);   // kept as a fallback; the server reads
      fd.append('body', body);    // X-CSRF-Token first because the global
      if (replyTo) fd.append('reply_to', replyTo);
      pending.forEach(function (p) { fd.append('attachments', p.file, p.file.name); });
      // CSRF middleware runs before multer, so req.body._csrf is empty on
      // multipart posts. The header path is what actually validates here.

      var url = '/org/' + orgSlug + '/projects/' + projectId
              + '/channels/' + channelId + '/messages';
      fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'X-CSRF-Token': csrf },
        body: fd
      }).then(function (r) {
        if (!r.ok) {
          return r.json().catch(function () { return { error: 'send failed (' + r.status + ')' }; })
                  .then(function (err) { throw new Error(err.error || ('HTTP ' + r.status)); });
        }
        return r.json();
      }).then(function (m) {
        if (homeMode) window.dispatchEvent(new CustomEvent('rushworks:home-sent', { detail: m }));
        else appendMessage(m, { scroll: true });
        textarea.value = '';
        setReply(null);
        pending.forEach(function (p) { URL.revokeObjectURL(p.previewUrl); });
        pending = [];
        renderTray();
      }).catch(function (err) {
        alert('Could not send: ' + err.message);
      }).finally(function () {
        sendBtn.disabled = false;
        textarea.focus();
      });
    }

    function deleteMessage(id) {
      var url = '/org/' + orgSlug + '/projects/' + projectId + '/messages/' + id + '/delete';
      var body = new URLSearchParams();
      body.set('_csrf', csrf);
      fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString()
      }).then(function (r) {
        if (!r.ok) throw new Error('delete failed: ' + r.status);
        return r.json();
      }).then(function () {
        tombstoneMessage(id);
      }).catch(function (err) {
        alert('Could not delete: ' + err.message);
      });
    }

    // ── Realtime ────────────────────────────────────────────────────────
    window.addEventListener('rushworks:message-event', function (e) {
      var p = e.detail;
      if (!p || homeMode) return;
      if (p.type === 'message_created' && p.message && p.message.channel_id === parseInt(channelId, 10)) {
        appendMessage(p.message, { flash: true });
      } else if (p.type === 'message_deleted' && p.channelId === parseInt(channelId, 10)) {
        tombstoneMessage(p.messageId);
      }
    });

    loadInitial();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
